import { Link } from '@medely/web-components';
import React, { ReactElement } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { MedelyLogo } from '@medely/web-components/icons';
import { useTheme } from '@medely/web-components/themes';
import { Box, Heading, Text } from '@medely/ui-kit';

const DefaultView = ({ theme }) => (
  <>
    <MedelyLogo color={theme.palette.primary.main} />
    <Box py={3}>
      <Heading size="xs">Thank you for your interest in joining Medely!</Heading>
    </Box>
    <Box pb={3}>
      <Text size="m">
        At this time, your application has not been accepted because you do not meet our
        requirements for work experience and/or education.
      </Text>
    </Box>
    <Box pb={3}>
      <Text size="m">
        For details about the specific requirements for the position you applied for, please review
        the relevant article in our{' '}
        <Link
          href="https://help.medely.com/en/collections/1942707-rn-allied-position-descriptions"
          target="_blank"
        >
          Help Center
        </Link>
        .
      </Text>
    </Box>
    <Box pb={3}>
      <Text size="m">
        Once you feel that you meet the requirements, we welcome you to re-apply.
      </Text>
    </Box>
  </>
);

const NoMarketView = ({ theme }) => (
  <>
    <MedelyLogo color={theme.palette.primary.main} />
    <Box py={3}>
      <Heading size="xs">Thank you for signing up to become a Medely Professional.</Heading>
    </Box>
    <Box pb={3}>
      <Text size="m">
        At Medely, we strive to match talented professionals like you with the right opportunities.
        We don&apos;t currently have available shifts for your position within 80 miles of your
        current location, but this can change as we are constantly expanding.
      </Text>
    </Box>
    <Box pb={3}>
      <Text size="m">
        Rest assured, we&apos;ve saved your profile and will email you as soon as shifts become
        available to resume your sign-up process.
      </Text>
    </Box>
  </>
);

const LowIncrementalityView = ({ theme }) => (
  <>
    <MedelyLogo color={theme.palette.primary.main} />
    <Box py={3}>
      <Heading size="xs">Thank you for signing up to become a Medely Professional.</Heading>
    </Box>
    <Box pb={3}>
      <Text size="m">
        At Medely, we review profiles on a rolling basis and accept new professionals based on
        marketplace needs. Due to a high number of registrations, we are not accepting new sign-ups
        in your zip code or surrounding metro areas.
      </Text>
    </Box>
    <Box pb={3}>
      <Text size="m">
        We are always expanding, and your profile has been saved in our system. We will notify you
        via email as soon as sign-ups reopen in your area.
      </Text>
    </Box>
  </>
);

const ProArchivedView = (): ReactElement => {
  const { currentUser } = useCurrentUser();
  const theme = useTheme();

  // note: in PRO-2863, archive_reason will become an enum instead of a string
  switch (currentUser?.professional?.archive_reason) {
    case 'dormant_market':
    case 'unavailable_market':
      return <NoMarketView theme={theme} />;
    case 'low_incrementality_market':
      return <LowIncrementalityView theme={theme} />;
    default:
      return <DefaultView theme={theme} />;
  }
};

export default ProArchivedView;
